import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import RegistrasiService from "../../../../services/registrasi/RegistrasiService.js";

// Mode ditentukan oleh flag yang di-set halaman pemanggil SEBELUM masuk ke edit-trader:
//   localStorage.setItem('editTraderMode', 'verifikasi')   -> tampilkan tombol verifikasi + email
//   localStorage.setItem('editTraderMode', 'edit')         -> hanya update data, tanpa email
const getMode = () => localStorage.getItem("editTraderMode") || "edit";

export const useEditTrader = () => {
    const navigate = useNavigate();
    const RegService = useMemo(() => new RegistrasiService(), []);
    const mode = getMode(); // 'verifikasi' | 'edit'

    const [trader, setTrader] = useState({});
    const [user, setUser] = useState({});
    const [traderUpt, setTraderUpt] = useState({});
    const [existTraderUpt, setExistTraderUpt] = useState(false);

    const [propinsi, setPropinsi] = useState([]);
    const [kota, setKota] = useState([]);
    const [namaUPT, setNamaUPT] = useState("");
    const [kodeUPT, setKodeUPT] = useState("");
    const [kodeUptAwal, setKodeUptAwal] = useState("");

    const [services, setServices] = useState({ first: false, second: false });
    const [layanFound, setLayananFound] = useState(false);
    const [noLayanan, setNoLayanan] = useState(0);

    const [showUPT, setShowUPT] = useState(false);
    const [showImg, setShowImg] = useState(false);
    const [jenisImg, setJenisImg] = useState("a");
    const [kdDokumen, setKdDokumen] = useState(0);

    // ── Ganti dokumen (replace file lama dengan yang baru) ──
    const [isReplacingDoc, setIsReplacingDoc] = useState(false);

    const [rejectReason, setRejectReason] = useState("");
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [alerts, setAlerts] = useState({
        pilihUPT: false,
        pilihService: false,
        alasanKosong: false,
        fileTooLarge: false,
        dokumenTerganti: false,
        gagalGantiDokumen: false,
        selesaiEdit: false,      // update biasa berhasil, tanpa email
        selesaiApprove: false,   // verifikasi disetujui, email terkirim
        selesaiReject: false,    // verifikasi ditolak, email terkirim
        serverError: false,
    });
    const setAlert = (key, value) => setAlerts((prev) => ({ ...prev, [key]: value }));

    // ── Guard akses role ──────────────────────────
    useEffect(() => {
        const roleUser = localStorage.getItem("role");
        if (roleUser !== "1" && roleUser !== "2") {
            navigate("/dashboard");
        }
    }, [navigate]);

    // ── Load data awal ────────────────────────────
    useEffect(() => {
        const init = async () => {
            const kodeTrader = localStorage.getItem("editTraderStorage");
            try {
                const resTrader = await RegService.getTrader(kodeTrader);
                setTrader(resTrader.data);
                setJenisImg(resTrader.data.FILE_ID);

                const resUser = await RegService.getUser(resTrader.data.KODE_TRADER);
                setUser(resUser.data);

                const resProp = await RegService.getPropinsi();
                setPropinsi(resProp.data);

                const resKota = await RegService.getKota(resTrader.data.PROPINSI);
                setKota(resKota.data);

                const resUpt1 = await RegService.getKdUptByTrader(resTrader.data.KODE_TRADER);
                if (resUpt1.data != null) {
                    const resUpt2 = await RegService.getUptById(resUpt1.data.KD_UNIT);
                    setTraderUpt(resUpt1.data);
                    setExistTraderUpt(true);
                    setKodeUPT(resUpt2.data.KD_UNIT);
                    setNamaUPT(resUpt2.data.NM_PENDEK_BARU);
                    setKodeUptAwal(resUpt2.data.KD_UNIT);
                }

                const resLayanan = await RegService.getLayanan(resTrader.data.KODE_TRADER);
                if (resLayanan.data != null) {
                    setLayananFound(true);
                    setNoLayanan(resLayanan.data.KODE);
                    if (resLayanan.data.KODE_LAYANAN === "3") {
                        setServices({ first: true, second: true });
                    } else if (resLayanan.data.KODE_LAYANAN === "1") {
                        setServices({ first: true, second: false });
                    } else {
                        setServices({ first: false, second: true });
                    }
                }
            } catch (error) {
                console.error("[useEditTrader init]", error);
            }
        };
        init();
    }, []);

    const handlePropinsiChange = async (e) => {
        setTrader((prev) => ({ ...prev, PROPINSI: e.target.value }));
        try {
            const res = await RegService.getKota(e.target.value);
            setKota(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    const getKdUPT = () => setKodeUPT(kodeUPT);

    const handleClickImg = (jnsImg, kdDok) => {
        setJenisImg(jnsImg);
        setKdDokumen(kdDok);
        setShowImg(true);
    };

    // ── Ganti dokumen lama (NPWP/KTP/NIB) dengan file baru ──
    // dokKode: 1 = NPWP, 2 = KTP/Identitas, 3 = NIB
    const MAX_DOC_SIZE = 1 * 1024 * 1024; // 1MB, sama seperti validasi di registrasi

    const replaceDocument = async (dokKode, file) => {
        if (!file) return;

        if (file.size > MAX_DOC_SIZE) {
            setAlert("fileTooLarge", true);
            return;
        }

        setIsReplacingDoc(true);
        try {
            // 1. Upload file baru ke file server (endpoint sama seperti registrasi)
            await RegService.putDokumen(trader.FILE_ID, file);

            // 2. Hitung path baru, lalu update record Tb_trader_dok yang sudah ada
            const newPath = `${process.env.REACT_APP_API_URL_FILE}/view/${trader.FILE_ID}/${file.name}`;
            await RegService.updateDokumen(trader.FILE_ID, dokKode, newPath);

            setAlert("dokumenTerganti", true);
        } catch (error) {
            console.error("[replaceDocument]", error);
            setAlert("gagalGantiDokumen", true);
        } finally {
            setIsReplacingDoc(false);
        }
    };

    // ── Simpan data trader/user/UPT/layanan (dipakai di SEMUA mode) ──
    // CATATAN: trader yang dibuat lewat alur registerFull sudah otomatis
    // punya record Tb_trader_upt & Tb_r_layanan sejak awal, jadi di sini
    // kita hanya UPDATE, bukan create baru. Kalau ketemu data lama yang
    // belum punya salah satunya (legacy), kita hentikan & beri tahu admin
    // daripada memanggil endpoint yang sudah tidak ada.
    const persistTraderData = async () => {
        if (!existTraderUpt) {
            throw new Error(
                "LEGACY_NO_UPT: Data trader ini belum memiliki record UPT (data lama). " +
                "Perlu penanganan manual di database sebelum bisa diverifikasi/diedit di sini."
            );
        }
        if (!layanFound) {
            throw new Error(
                "LEGACY_NO_LAYANAN: Data trader ini belum memiliki record layanan (data lama). " +
                "Perlu penanganan manual di database sebelum bisa diverifikasi/diedit di sini."
            );
        }

        await RegService.updateTrader(trader, trader.KODE_TRADER);
        await RegService.updateUser(user, user.USER_ID);

        const nowPayload = {
            KODE_TRADER: trader.KODE_TRADER,
            KD_UNIT: kodeUPT,
            KODE_TRADER_LOKAL: null,
            STATUS: "2",
            TGL_AKTIF: new Date().toISOString().slice(0, 19).replace("T", " "),
            USER_UPDATE: localStorage.getItem("userId"),
            DATE_CREATED: new Date().toISOString().slice(0, 19).replace("T", " "),
        };
        await RegService.updateTraderUPT(nowPayload, traderUpt.KODE_TRADER, kodeUptAwal);

        const kodeLayanan = services.first && services.second ? "3" : services.first ? "1" : "2";
        await RegService.updateLayanan(
            { KODE_TRADER: user.KODE_TRADER, KODE_LAYANAN: kodeLayanan, STS_VERIFIKASI: "1" },
            noLayanan
        );
    };

    const validateBeforeSave = () => {
        if (!kodeUPT) {
            setAlert("pilihUPT", true);
            return false;
        }
        if (!services.first && !services.second) {
            setAlert("pilihService", true);
            return false;
        }
        return true;
    };

    // ── Mode 'edit': update data biasa, TIDAK kirim email ──
    const saveEditOnly = async (e) => {
        e.preventDefault();
        if (!validateBeforeSave()) return;

        setIsSubmitting(true);
        try {
            await persistTraderData();
            setAlert("selesaiEdit", true);
        } catch (error) {
            console.error("[saveEditOnly]", error);
            setAlert("serverError", true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Mode 'verifikasi': setujui + kirim email ──
    const approveVerification = async (e) => {
        e.preventDefault();
        if (!validateBeforeSave()) return;

        setIsSubmitting(true);
        try {
            await persistTraderData();
            await RegService.verifyApprove(trader.KODE_TRADER);
            setAlert("selesaiApprove", true);
        } catch (error) {
            console.error("[approveVerification]", error);
            setAlert("serverError", true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Mode 'verifikasi': tolak/tunda + kirim email berisi alasan ──
    const rejectVerification = async (e) => {
        e.preventDefault();
        if (!rejectReason.trim()) {
            setAlert("alasanKosong", true);
            return;
        }

        setIsSubmitting(true);
        try {
            await persistTraderData();
            await RegService.verifyReject(trader.KODE_TRADER, rejectReason.trim());
            setShowRejectForm(false);
            setAlert("selesaiReject", true);
        } catch (error) {
            console.error("[rejectVerification]", error);
            setAlert("serverError", true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseAfterDone = () => {
        navigate("/setting/trader");
    };

    return {
        mode,
        trader, setTrader,
        user, setUser,
        propinsi, kota,
        namaUPT, setNamaUPT,
        kodeUPT, setKodeUPT,
        getKdUPT,
        services, setServices,
        showUPT, setShowUPT,
        showImg, setShowImg,
        jenisImg, kdDokumen,
        handleClickImg,
        isReplacingDoc,
        replaceDocument,
        handlePropinsiChange,
        rejectReason, setRejectReason,
        showRejectForm, setShowRejectForm,
        isSubmitting,
        alerts, setAlert,
        saveEditOnly,
        approveVerification,
        rejectVerification,
        handleCloseAfterDone,
        navigate,
    };
};